import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { selectBrand, selectMenu, useAppSelector } from '@open-tender/cloud'
import {
  fetchCustomerOrders,
  selectCustomer,
  selectCustomerOrders,
  selectMenuSlug,
  useAppDispatch,
} from '@open-tender/cloud'
import {
  makeMenuItemLookup,
  makeRecents,
  makeUniqueDisplayItems,
} from '@open-tender/utils'
import { Content, Loading, Main, ScreenreaderTitle } from 'components'
import {
  MenuCategoryHeader,
  MenuHeader,
  MenuItems,
  MenuItem,
  MenuCategoryView,
  MenuCategoryWithHeaderView,
} from '../Menu'
import CategorySwitch from '../Category/CategorySwitch'
import { sendMenuView } from 'app/analytics'
import { useMenuFilters } from 'utils/helpers'

const MenuRecents = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { title: siteTitle } = useAppSelector(selectBrand) || {}
  const { categories, soldOut } = useAppSelector(selectMenu)
  const menuSlug = useAppSelector(selectMenuSlug)
  const { auth } = useAppSelector(selectCustomer)
  const hasCustomer = !!auth
  const itemLookup = useMemo(() => makeMenuItemLookup(categories), [categories])
  const orders = useAppSelector(selectCustomerOrders)
  const { filterItems } = useMenuFilters()
  const displayItems = useMemo(
    () => makeUniqueDisplayItems(orders.entities),
    [orders.entities]
  )
  const recents = useMemo(
    () =>
      filterItems(makeRecents(displayItems, itemLookup, soldOut)).map(
        (i, index) => ({
          ...i,
          list_id: 'recents',
          list_name: 'Recents',
          menu_position: index + 1,
        })
      ),
    [displayItems, filterItems, itemLookup, soldOut]
  )
  const hasRecents = recents && recents.length > 0
  const showLoading = orders.loading === 'pending' && !hasRecents

  const shouldRedirect =
    !hasCustomer || (!hasRecents && orders.loading !== 'pending')

  useEffect(() => {
    if (hasCustomer) {
      dispatch(fetchCustomerOrders(11))
    }
  }, [dispatch, hasCustomer])

  useEffect(() => {
    if (shouldRedirect) navigate(menuSlug)
  }, [navigate, shouldRedirect, menuSlug])

  useEffect(() => {
    sendMenuView({
      category: { name: 'Recents' },
      items: recents,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (showLoading) return null

  return (
    <>
      <Helmet>
        <title>Recents | {siteTitle}</title>
      </Helmet>
      <Content scrollTop={false}>
        <MenuHeader />
        <Main>
          <ScreenreaderTitle>Recents</ScreenreaderTitle>
          <CategorySwitch />
          <MenuCategoryWithHeaderView>
            <MenuCategoryView>
              <MenuCategoryHeader
                title="Recents"
                subtitle="All of the items you've ordered across your last 10 orders"
              />
              {showLoading ? (
                <Loading />
              ) : (
                <MenuItems>
                  {recents.map((item, index) => (
                    <MenuItem
                      key={`${item.id}-${index}`}
                      item={item}
                      favorite={item.favorite}
                    />
                  ))}
                </MenuItems>
              )}
            </MenuCategoryView>
          </MenuCategoryWithHeaderView>
        </Main>
      </Content>
    </>
  )
}

export default MenuRecents
